import { TFunction } from 'i18next';
import {
  ChatEligibleUserRole,
  ChatItemKind,
  ChatAsset,
  ChatLibraryEntry,
  ChatLibrarySourceType,
  ChatParticipant,
  ChatSummary,
  ChatTimelineItem,
} from '../models/Chat';

const CHAT_AVATAR_COLORS = ['#4E67EB', '#059669', '#C97B1E', '#8B5CF6', '#E85D75', '#0EA5E9'];
const MESSAGE_GROUP_WINDOW_MS = 5 * 60 * 1000;

/** Stable key for masked Band Space Support authors (`authorUserId` null). */
export const CHAT_SUPPORT_AUTHOR_KEY = '__band_space_support__';

export function chatAvatarColor(userId: string | null | undefined): string {
  const key = userId?.trim() || CHAT_SUPPORT_AUTHOR_KEY;
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CHAT_AVATAR_COLORS[Math.abs(hash) % CHAT_AVATAR_COLORS.length];
}

export function formatChatMessageTime(iso: string, locale: string): string {
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(new Date(normalized));
}

export function chatMessageDayKey(iso: string): string {
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  const date = new Date(normalized);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function formatChatDayDivider(iso: string, locale: string, t: TFunction): string {
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  const date = new Date(normalized);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (isSameCalendarDay(date, today)) {
    return t('chat.timeline.today', 'Today');
  }
  if (isSameCalendarDay(date, yesterday)) {
    return t('chat.timeline.yesterday', 'Yesterday');
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

export function shouldShowChatMessageHeader(
  current: { authorUserId: string | null; createdAt: string },
  previous: { authorUserId: string | null; createdAt: string } | undefined
): boolean {
  if (!previous) return true;
  if (current.authorUserId !== previous.authorUserId) return true;

  const prevTime = new Date(
    previous.createdAt.endsWith('Z') ? previous.createdAt : `${previous.createdAt}Z`
  ).getTime();
  const currTime = new Date(
    current.createdAt.endsWith('Z') ? current.createdAt : `${current.createdAt}Z`
  ).getTime();
  return currTime - prevTime > MESSAGE_GROUP_WINDOW_MS;
}

export function formatChatTimestamp(iso: string, locale: string): string {
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(normalized));
}

export function formatChatDueDate(iso: string | null | undefined, locale: string): string | null {
  if (!iso) return null;
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(normalized)
  );
}

export function formatFileSize(bytes: number | null | undefined, locale: string): string | null {
  if (bytes == null || Number.isNaN(bytes) || bytes < 0) return null;
  const mb = bytes / (1024 * 1024);
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(mb);
  return `${formatted} MB`;
}

const CHAT_IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'svg',
  'heic',
  'heif',
  'avif',
]);

function chatFileExtension(value: string): string | undefined {
  const path = value.split('?')[0]?.split('#')[0] ?? value;
  return path.split('.').pop()?.toLowerCase();
}

export function isChatImageAsset(
  asset: Pick<ChatAsset, 'kind' | 'mimeType' | 'url' | 'title'>
): boolean {
  if (asset.kind === 'LINK') return false;
  if (asset.mimeType?.startsWith('image/')) return true;
  const ext = chatFileExtension(asset.title) ?? chatFileExtension(asset.url);
  return ext != null && CHAT_IMAGE_EXTENSIONS.has(ext);
}

/** Attached asset IDs in display order; falls back to legacy `assetId`. */
export function chatTimelineAssetIds(item: Pick<ChatTimelineItem, 'assetId' | 'assetIds'>): string[] {
  if (item.assetIds?.length) return item.assetIds;
  if (item.assetId) return [item.assetId];
  return [];
}

/** Body text to show in the timeline; omits filenames already shown under attachments. */
export function chatTimelineDisplayBody(
  item: Pick<ChatTimelineItem, 'body' | 'assetId' | 'assetIds'>,
  assetsById: ReadonlyMap<string, ChatAsset>
): string | null {
  const body = item.body?.trim();
  if (!body) return null;

  const attachments = chatTimelineAssetIds(item)
    .map((id) => assetsById.get(id))
    .filter((asset): asset is ChatAsset => Boolean(asset));

  if (!attachments.length) return body;

  const bodyLower = body.toLowerCase();
  const duplicatesAttachmentTitle = attachments.some(
    (asset) => asset.title.trim().toLowerCase() === bodyLower
  );

  return duplicatesAttachmentTitle ? null : body;
}

export function chatLibrarySourceKey(
  sourceType: ChatLibrarySourceType,
  sourceId: string
): string {
  return `${sourceType}:${sourceId}`;
}

export function isChatLibrarySaved(
  savedKeys: ReadonlySet<string>,
  sourceType: ChatLibrarySourceType,
  sourceId: string
): boolean {
  return savedKeys.has(chatLibrarySourceKey(sourceType, sourceId));
}

export function findChatLibraryEntry(
  entries: readonly ChatLibraryEntry[],
  sourceType: ChatLibrarySourceType,
  sourceId: string
): ChatLibraryEntry | undefined {
  return entries.find(
    (entry) => entry.sourceType === sourceType && entry.sourceId === sourceId
  );
}

/** Resolve a library asset entry to a chat asset for previews (falls back to entry fields). */
export function chatLibraryEntryAsAsset(
  entry: ChatLibraryEntry,
  assetsById: ReadonlyMap<string, ChatAsset>
): ChatAsset | null {
  if (entry.sourceType !== 'ASSET' || !entry.url) {
    return null;
  }

  return (
    assetsById.get(entry.sourceId) ?? {
      id: entry.sourceId,
      kind: entry.assetKind ?? 'FILE',
      uploadedByUserId: '',
      uploadedByName: entry.sourceAuthorName,
      sharedInItemId: '',
      title: entry.title ?? entry.url,
      description: entry.description,
      url: entry.url,
      mimeType: null,
      sizeBytes: null,
      createdAt: entry.createdAt,
    }
  );
}

export function isTimelineFeedKind(kind: ChatItemKind): boolean {
  return kind === 'MESSAGE' || kind === 'NOTE' || kind === 'FILE_SHARED' || kind === 'SYSTEM';
}

/**
 * Authoritative participant list. Falls back to participantOne/Two when the
 * API omits `participants` (legacy payloads).
 */
export function chatParticipants(
  summary: Pick<
    ChatSummary,
    | 'participants'
    | 'participantOneUserId'
    | 'participantOneName'
    | 'participantOneProfileImageUrl'
    | 'participantTwoUserId'
    | 'participantTwoName'
    | 'participantTwoProfileImageUrl'
  >
): ChatParticipant[] {
  if (summary.participants?.length) {
    return summary.participants;
  }

  return [
    {
      userId: summary.participantOneUserId ?? '',
      name: summary.participantOneName,
      profileImageUrl: summary.participantOneProfileImageUrl,
    },
    {
      userId: summary.participantTwoUserId ?? '',
      name: summary.participantTwoName,
      profileImageUrl: summary.participantTwoProfileImageUrl,
    },
  ].filter((participant, index, list) => {
    if (!participant.userId) return false;
    return list.findIndex((entry) => entry.userId === participant.userId) === index;
  });
}

/**
 * Organization-scoped chats (staff roster or Band Space support). Prefer API
 * `kind`; fall back to participant count &gt; 2 when kind is absent.
 * Individual `SUPPORT_CASE` threads are not organization-scoped.
 */
export function isOrganizationScopedChat(
  summary: Pick<ChatSummary, 'kind' | 'participants'>
): boolean {
  if (summary.kind === 'ORGANIZATION_TEAM' || summary.kind === 'ORGANIZATION_SUPPORT') {
    return true;
  }
  if (summary.kind === 'DIRECT' || summary.kind === 'SUPPORT_CASE') {
    return false;
  }
  return (summary.participants?.length ?? 0) > 2;
}

export function isOrganizationSupportChat(
  summary: Pick<ChatSummary, 'kind'>
): boolean {
  return summary.kind === 'ORGANIZATION_SUPPORT';
}

/** One conversation per individual support request (not Band Space Team). */
export function isSupportCaseChat(summary: Pick<ChatSummary, 'kind'>): boolean {
  return summary.kind === 'SUPPORT_CASE';
}

/** One-person staff chats (individual tutors) are not shown as My Team. */
export function isSoloOrganizationStaffChat(
  summary: Pick<ChatSummary, 'kind' | 'participants'>,
  currentUserId: string | undefined
): boolean {
  if (summary.kind !== 'ORGANIZATION_TEAM') return false;
  const others = (summary.participants ?? []).filter((member) => member.userId !== currentUserId);
  return others.length === 0;
}

/**
 * True when this support chat belongs to the viewer's organization.
 * Role-scoped list endpoints already apply this; kept for tests and local checks.
 */
export function isOwnOrganizationSupportChat(
  summary: Pick<ChatSummary, 'kind' | 'organizationId'>,
  viewerOrganizationId: string | null | undefined
): boolean {
  if (!isOrganizationSupportChat(summary)) return false;
  return Boolean(viewerOrganizationId && summary.organizationId === viewerOrganizationId);
}

/** Team and support chats stay pinned at the top of the chat list. */
export function isPinnedChat(
  summary: Pick<ChatSummary, 'kind' | 'participants'>
): boolean {
  return isOrganizationScopedChat(summary);
}

function chatListPinRank(
  summary: Pick<ChatSummary, 'kind' | 'participants'>
): number {
  if (isOrganizationSupportChat(summary)) return 0;
  if (isOrganizationScopedChat(summary)) return 1;
  return 2;
}

function chatActivityTimestampMs(iso: string): number {
  const normalized = iso.endsWith('Z') ? iso : `${iso}Z`;
  return new Date(normalized).getTime();
}

/** Band Space Team, then My Team, then remaining chats by last activity. */
export function compareChatListOrder(
  a: Pick<ChatSummary, 'kind' | 'participants' | 'lastActivityAt'>,
  b: Pick<ChatSummary, 'kind' | 'participants' | 'lastActivityAt'>
): number {
  const rankDiff = chatListPinRank(a) - chatListPinRank(b);
  if (rankDiff !== 0) return rankDiff;
  return chatActivityTimestampMs(b.lastActivityAt) - chatActivityTimestampMs(a.lastActivityAt);
}

/** @deprecated Prefer isOrganizationScopedChat / isOrganizationSupportChat */
export function isOrganizationTeamChat(
  summary: Pick<ChatSummary, 'kind' | 'participants'>
): boolean {
  if (summary.kind === 'ORGANIZATION_TEAM') return true;
  if (
    summary.kind === 'ORGANIZATION_SUPPORT' ||
    summary.kind === 'DIRECT' ||
    summary.kind === 'SUPPORT_CASE'
  ) {
    return false;
  }
  return (summary.participants?.length ?? 0) > 2;
}

function participantLabel(member: ChatParticipant): string {
  return member.name?.trim() || member.userId;
}

function formatPartnerName(member: ChatParticipant, t?: TFunction): string {
  const name = participantLabel(member);
  if (!member.deleted) {
    return name;
  }
  return t
    ? t('chat.partner.deactivated', '{{name}} (deactivated)', { name })
    : `${name} (deactivated)`;
}

export function chatPartnerLabel(
  summary: Parameters<typeof chatParticipants>[0] & Pick<ChatSummary, 'kind'>,
  currentUserId: string | undefined,
  t?: TFunction
): string {
  const members = chatParticipants(summary);
  if (isOrganizationScopedChat(summary)) {
    const others = members.filter((member) => member.userId !== currentUserId);
    const names = others.map((member) => formatPartnerName(member, t)).filter(Boolean);
    if (names.length <= 2) {
      return names.join(' · ') || (isOrganizationSupportChat(summary) ? 'Band Space Team' : 'My Team');
    }
    return `${names.slice(0, 2).join(' · ')} +${names.length - 2}`;
  }

  if (!currentUserId) {
    return members.map((member) => formatPartnerName(member, t)).join(' · ') || 'Chat';
  }

  const other = members.find((member) => member.userId !== currentUserId);
  if (other) {
    return formatPartnerName(other, t);
  }
  return t
    ? t('chat.partner.unavailable', 'Deactivated user')
    : 'Deactivated user';
}

/** Display title for list/detail headers (support / team / direct / case). */
export function chatTitleLabel(
  summary: Parameters<typeof chatPartnerLabel>[0] &
    Pick<ChatSummary, 'kind' | 'organizationName' | 'id'>,
  currentUserId: string | undefined,
  t: TFunction,
  options?: { inbox?: 'admin' | 'member'; supportCaseSubject?: string | null }
): string {
  if (isSupportCaseChat(summary)) {
    const subject = options?.supportCaseSubject?.trim();
    if (subject) return subject;
    return t('supportCases.untitled', 'Support case');
  }
  if (isOrganizationSupportChat(summary)) {
    if (options?.inbox === 'admin') {
      return (
        summary.organizationName?.trim() ||
        t('chat.support.organizationFallback', 'Organization')
      );
    }
    return t('chat.support.title', 'Band Space Team');
  }
  if (isOrganizationScopedChat(summary)) {
    return t('chat.team.title', 'My Team');
  }
  return chatPartnerLabel(summary, currentUserId, t);
}

export type ChatInbox = 'user' | 'host' | 'tutor' | 'admin';

export type ChatInboxBasePath = '/user/chats' | '/tutor/chats' | '/host/chats' | '/admin/chats';

export function chatBasePath(pathname: string): ChatInboxBasePath {
  if (pathname.startsWith('/tutor/chats')) {
    return '/tutor/chats';
  }
  if (pathname.startsWith('/host/chats')) {
    return '/host/chats';
  }
  if (pathname.startsWith('/admin/chats')) {
    return '/admin/chats';
  }
  return '/user/chats';
}

export function chatInboxFromPath(pathname: string): ChatInbox {
  const base = chatBasePath(pathname);
  if (base === '/tutor/chats') return 'tutor';
  if (base === '/host/chats') return 'host';
  if (base === '/admin/chats') return 'admin';
  return 'user';
}

export function isAdminChatInbox(pathname: string): boolean {
  return chatInboxFromPath(pathname) === 'admin';
}

export function chatMemberCountLabel(
  summary: Parameters<typeof chatParticipants>[0] & Pick<ChatSummary, 'kind'>,
  t: TFunction
): string | null {
  if (!isOrganizationScopedChat(summary)) return null;
  const count = chatParticipants(summary).length;
  return t('chat.team.memberCount', '{{count}} members', { count });
}

export function chatCounterpartyUserId(
  summary: Parameters<typeof chatParticipants>[0],
  currentUserId: string | undefined
): string {
  const members = chatParticipants(summary);
  const other = members.find((member) => member.userId !== currentUserId);
  if (other?.userId) {
    return other.userId;
  }
  return members[0]?.userId ?? '';
}

/**
 * Primary role for display chips/tooltips. Prefers ADMIN → HOST → TUTOR;
 * omits plain USER. Display only — never use for eligibility.
 */
export function chatParticipantDisplayRole(
  member: Pick<ChatParticipant, 'roles'>
): ChatEligibleUserRole | null {
  const roles = member.roles ?? [];
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('HOST')) return 'HOST';
  if (roles.includes('TUTOR')) return 'TUTOR';
  return null;
}

export function chatParticipantRoleLabel(
  role: ChatEligibleUserRole,
  t: TFunction
): string {
  switch (role) {
    case 'ADMIN':
      return t('chat.roles.admin', 'Admin');
    case 'HOST':
      return t('chat.roles.host', 'Host');
    case 'TUTOR':
      return t('chat.roles.tutor', 'Tutor');
    case 'USER':
      return t('chat.roles.user', 'User');
    default:
      return role;
  }
}

export type ChatCreateRole = 'tutor' | 'student';

/** Role is fixed by route: tutor menu vs user menu. Host chats use student create semantics. */
export function chatCreateRoleFromPath(pathname: string): ChatCreateRole {
  return chatBasePath(pathname) === '/tutor/chats' ? 'tutor' : 'student';
}
